import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { track, PLAN_VALUE } from "@/lib/analytics";
import { pickSampleSlug } from "@/lib/sample";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Minus, ArrowRight, Info, ShieldCheck } from "lucide-react";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { PublicAmbient, KineticHeading } from "@/components/public/PublicVisuals";

function pounds(pence) {
  if (pence == null) return null;
  const v = pence / 100;
  return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}`;
}

// What the £29 Prove plan delivers — mirrors the paid Donkey's Verdict structure.
const PROVE_VALUE = [
  "A personalised opportunity profile",
  "Three business opportunities built around you",
  "Honest viability analysis",
  "A recommended strongest opportunity",
  "The complete Donkey's Verdict",
  "The first month of validation support",
];

// Short outcome + a curated 5–7 headline benefits per plan. Full detail lives in the table.
const OUTCOME = {
  free: "Explore the whole product with fictional demonstrations — no personalised AI.",
  prove: "Your complete Donkey's Verdict and your first month of validation in one plan.",
  build: "Everything in Prove, plus the tools to build one business properly.",
  launch: "Everything in Build, plus launch and growth marketing (ad spend excluded).",
  grow: "Everything in Launch, plus CRM, retention and analytics across up to three businesses.",
  scale: "Everything in Grow, plus advanced permissions and cross-business reporting across ten businesses.",
  enterprise: "Configurable limits, controls and support for larger operations.",
};
const BENEFITS = {
  free: ["Interactive example verdicts", "Deterministic Donkey Check", "Product tour & methodology", "Preview of a paid report", "Full pricing"],
  prove: ["Complete adaptive AI interview & verdict", "Three personalised opportunities", "Live market research", "Validation workspace & experiments", "Evidence recording", "Proceed / Pivot / Stop decisions"],
  build: ["Everything in Prove", "Brand, logo & identity", "Live website", "Offer & pricing development", "Domain, payments & booking setup", "SEO foundations", "Legal-document templates"],
  launch: ["Everything in Build", "Launch strategy & campaign", "SEO & answer-engine content", "Email sequences", "Social content engine", "Scheduling & publishing", "Performance reporting"],
  grow: ["Everything in Launch", "CRM & lead nurturing", "Customer onboarding & delivery", "Support inbox & DonkeyBot", "Retention & recurring campaigns", "Advanced analytics & unit economics", "Up to 3 businesses"],
  scale: ["Everything in Grow", "Advanced team permissions", "Cross-business reporting", "Higher automation limits", "Priority support", "Up to 10 businesses"],
  enterprise: ["Everything in Scale", "White labelling & API access", "Bespoke integrations", "Custom AI & research allowances", "Organisation controls", "Dedicated onboarding & support"],
};

const AVAIL = {
  available: ["Available now", "text-donkey-green border-donkey-green/40"],
  billing_not_live: ["Built · billing not live", "text-donkey-amber border-donkey-amber/40"],
  contact: ["Contact us · setup required", "text-donkey-grey border-white/25"],
  planned: ["Planned", "text-donkey-grey border-white/20"],
};

function cellValue(v) {
  if (v === "yes") return <Check className="w-4 h-4 text-donkey-green mx-auto" />;
  if (v === "no" || v === 0) return <Minus className="w-4 h-4 text-donkey-grey/50 mx-auto" />;
  if (v === "unlimited") return <span className="text-donkey-orange text-xs">Unlimited</span>;
  return <span className="text-donkey-warm text-sm font-mono">{v}</span>;
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/plans").then(({ data }) => setData(data)).catch(() => {}); }, []);
  useEffect(() => { track("view_pricing"); }, []);

  const PAID = ["prove", "build", "launch", "grow", "scale"];

  const startCheckout = async (planId) => {
    if (!PAID.includes(planId)) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/pricing?buy=${planId}`)}`);
      return;
    }
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout", { plan: planId, origin_url: window.location.origin });
      if (data?.checkout_url) {
        track("begin_checkout", { plan: planId, value: PLAN_VALUE[planId] });
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) toast.info("Checkout is opening shortly — please try again in a moment.");
      else if (status === 401) navigate(`/login?next=${encodeURIComponent(`/pricing?buy=${planId}`)}`);
      else toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
    } finally {
      setBusy("");
    }
  };

  // Resume a selected plan after sign-up / login (?buy=<plan>).
  useEffect(() => {
    const buy = params.get("buy");
    if (buy && user && PAID.includes(buy)) startCheckout(buy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, data]);

  const cta = (p) => {
    if (p.id === "free") { navigate("/meet-the-donkey"); return; }
    if (p.id === "enterprise") { navigate("/contact"); return; }
    track("select_plan", { plan: p.id, value: PLAN_VALUE[p.id] });
    startCheckout(p.id);
  };

  if (!data) return <div className="min-h-[50vh]" />;
  const plans = {};
  data.plans.forEach((p) => (plans[p.id] = p));
  const order = data.order;
  const journey = ["prove", "build", "launch", "grow", "scale"].filter((id) => plans[id]);

  const PriceLine = ({ p }) => {
    if (p.id === "free") return <p className="font-mono text-4xl text-donkey-warm">£0</p>;
    if (p.kind === "contact") return <p className="font-mono text-3xl text-donkey-warm">Talk to us</p>;
    const amount = annual ? p.price_year : p.price_month;
    return (
      <div>
        <p className="font-mono text-4xl text-donkey-warm">{pounds(amount)}<span className="text-sm text-donkey-grey font-sans">{annual ? "/year" : "/month"}</span></p>
        {p.id === "prove" && !annual && <p className="text-xs text-donkey-grey mt-1">£29 first month, then £29/month unless cancelled</p>}
        {p.id !== "prove" && !annual && <p className="text-xs text-donkey-grey mt-1">Recurring monthly subscription · cancel anytime</p>}
        {annual && <p className="text-xs text-donkey-green mt-1">≈ two months free vs monthly</p>}
      </div>
    );
  };

  const Card = ({ id }) => {
    const p = plans[id];
    const entry = p.id === "prove";
    return (
      <div className={`group relative overflow-hidden rounded-2xl p-6 flex flex-col h-full transition-all duration-300 ${entry ? "border-2 border-donkey-orange bg-donkey-deep shadow-[0_0_50px_rgba(249,115,22,0.22)] lg:scale-[1.03]" : "card-donkey hover:-translate-y-1.5 hover:border-donkey-orange/50 hover:shadow-[0_0_34px_rgba(249,115,22,0.12)]"}`} data-testid={`plan-card-${id}`}>
        <span aria-hidden className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "radial-gradient(420px circle at 50% 0%, rgba(249,115,22,0.10), transparent 70%)" }} />
        {entry && <span className="brand-lightsweep" aria-hidden />}
        {entry && <span className="self-start -mt-9 mb-3 relative bg-donkey-orange text-donkey-black text-xs font-semibold px-3 py-1 rounded-full">Start here</span>}
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-heading font-semibold text-xl text-donkey-warm">{p.name}</h3>
          {p.availability && AVAIL[p.availability] && (
            <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 shrink-0 ${AVAIL[p.availability][1]}`} data-testid={`avail-${id}`}>{AVAIL[p.availability][0]}</span>
          )}
        </div>
        {p.question && <p className="text-sm text-donkey-warm/70 italic mt-1">“{p.question}”</p>}
        <div className="mt-4"><PriceLine p={p} /></div>
        <p className="text-sm text-donkey-grey mt-3 leading-relaxed min-h-[56px]">{OUTCOME[id]}</p>
        <Button
          onClick={() => cta(p)}
          disabled={busy === id}
          className={`mt-5 w-full font-semibold ${entry ? "bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange" : "bg-transparent border border-white/15 text-donkey-warm hover:bg-white/5"}`}
          data-testid={`plan-cta-${id}`}
        >
          {busy === id ? "Starting…" : p.cta} {entry && busy !== id && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
        {id === "prove" && (
          <>
            <p className="text-xs text-donkey-warm/80 mt-3 flex items-start gap-1.5" data-testid="prove-billing-copy">
              <ShieldCheck className="w-3.5 h-3.5 text-donkey-orange shrink-0 mt-0.5" />
              £29 per month. Cancel anytime securely through Stripe.
            </p>
            <button
              onClick={() => navigate(`/example/${pickSampleSlug()}`)}
              className="text-xs text-donkey-orange hover:underline mt-2 inline-flex items-center gap-1"
              data-testid="pricing-sample-verdict"
            >
              See a sample Donkey's Verdict <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <p className="text-xs text-donkey-grey mt-3" data-testid="prove-legacy-note">
              Existing Full Verdict purchasers keep permanent report access and any upgrade credit already earned.
            </p>
          </>
        )}
        <ul className="mt-5 space-y-2.5">
          {(BENEFITS[id] || []).map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm text-donkey-warm/85">
              <Check className="w-4 h-4 text-donkey-green shrink-0 mt-0.5" /><span>{b}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-donkey-grey mt-4 pt-3 border-t border-white/8">Full feature detail in the comparison below.</p>
      </div>
    );
  };

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.4} />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto">
        <DonkeyMascot size={124} alt="" className="mx-auto mb-5 opacity-95 donkey-alive drop-shadow-[0_0_50px_rgba(249,115,22,0.22)]" />
        <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-4">Pricing</p>
        <KineticHeading text="Start with Prove." className="text-4xl sm:text-5xl" />
        <p className="mt-5 text-donkey-grey">Explore for free, then start with Prove — your complete Donkey's Verdict and first month of validation for £29. Keep building, launching and growing as you go.</p>
        <p className="mt-3 text-sm text-donkey-amber/90 inline-flex items-center gap-2"><Info className="w-4 h-4" /> No free personalised AI — Free Preview is fictional and deterministic.</p>
      </div>

      {/* Billing toggle */}
      <div className="mt-10 flex items-center justify-center gap-3" data-testid="billing-toggle">
        <span className={`text-sm ${!annual ? "text-donkey-warm" : "text-donkey-grey"}`}>Monthly</span>
        <button onClick={() => setAnnual((a) => !a)} className="relative w-14 h-7 rounded-full bg-donkey-deep border border-white/15" aria-label="Toggle annual billing" data-testid="billing-toggle-btn">
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-donkey-orange transition-all ${annual ? "left-8" : "left-1"}`} />
        </button>
        <span className={`text-sm ${annual ? "text-donkey-warm" : "text-donkey-grey"}`}>Annual <span className="text-donkey-green text-xs">· 2 months free</span></span>
      </div>

      {/* Free preview */}
      <div className="mt-12 grid lg:grid-cols-3 gap-6 items-stretch">
        <Card id="free" />
        <div className="lg:col-span-2 rounded-2xl card-donkey p-6 flex flex-col sm:flex-row items-center gap-5">
          <DonkeyMascot size={92} alt="" className="shrink-0 opacity-95" />
          <div>
            <h3 className="font-heading font-semibold text-lg text-donkey-warm">The paid journey: Prove → Build → Launch → Grow → Scale</h3>
            <p className="text-sm text-donkey-grey mt-2">Everything begins with Prove. Each step includes the one before it, so you only ever add what you're ready for.</p>
          </div>
        </div>
      </div>

      {/* Journey tiers */}
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {journey.map((id) => <Card key={id} id={id} />)}
        {plans.enterprise && <Card id="enterprise" />}
      </div>

      {/* What you receive with Prove */}
      <div className="mt-10 card-donkey rounded-2xl p-7 border border-donkey-orange/25" data-testid="prove-value-block">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-heading font-bold text-2xl text-donkey-warm">What you receive with Prove</h2>
            <p className="text-sm text-donkey-grey mt-2">Your complete first month — everything you need to prove the right opportunity, built around you.</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {PROVE_VALUE.map((v) => (
                <div key={v} className="flex items-start gap-2.5 text-sm text-donkey-warm/90">
                  <Check className="w-4 h-4 text-donkey-green shrink-0 mt-0.5" /><span>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="shrink-0 md:text-right">
            <p className="font-heading font-extrabold text-4xl text-donkey-orange">£29<span className="text-lg text-donkey-warm/70">/month</span></p>
            <p className="text-xs text-donkey-grey mt-1">Cancel anytime securely through Stripe.</p>
            <Button
              onClick={() => cta(plans.prove)}
              disabled={busy === "prove"}
              className="mt-4 bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-semibold rounded-full px-7 h-11"
              data-testid="prove-value-cta"
            >
              {busy === "prove" ? "Starting…" : "Start with Prove — £29"} {busy !== "prove" && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
            <button
              onClick={() => navigate(`/example/${pickSampleSlug()}`)}
              className="block md:ml-auto text-xs text-donkey-orange hover:underline mt-3"
              data-testid="prove-value-sample-link"
            >
              See a sample Donkey's Verdict →
            </button>
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="mt-20">
        <h2 className="font-heading font-bold text-2xl text-donkey-warm mb-6">Compare every plan</h2>
        <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="comparison-table" role="region" aria-label="Plan feature comparison" tabIndex={0}>
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-donkey-deep">
                <th className="text-left p-4 text-donkey-grey font-medium sticky left-0 bg-donkey-deep">Feature</th>
                {order.map((id) => (
                  <th key={id} className="p-4 text-donkey-warm font-heading font-semibold text-center whitespace-nowrap">{plans[id].name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.comparison.map((row) => (
                <tr key={row.key} className="border-t border-white/8">
                  <td className="p-4 text-donkey-warm/85 sticky left-0 bg-donkey-bg">{row.label}</td>
                  {order.map((id) => <td key={id} className="p-4 text-center">{cellValue(row.values[id])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-donkey-grey mt-3">Stage 3–5 features are shown for transparency; each plan's availability is labelled honestly above.</p>
      </div>

      {/* Disclosures */}
      <div className="mt-16 card-donkey rounded-2xl p-7" data-testid="pricing-disclosures">
        <h3 className="font-heading font-semibold text-donkey-warm flex items-center gap-2 mb-4"><ShieldCheck className="w-5 h-5 text-donkey-orange" /> The honest small print</h3>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
          {data.disclosures.map((d) => (
            <li key={d} className="flex items-start gap-2 text-sm text-donkey-grey"><Minus className="w-4 h-4 text-donkey-orange shrink-0 mt-0.5" /> {d}</li>
          ))}
        </ul>
      </div>

      <p className="text-center text-xs text-donkey-grey mt-10 max-w-xl mx-auto">
        The Honest Donkey provides research and decision support, not financial, legal or professional advice. We never guarantee income, customers or business success.
      </p>
      </div>
    </div>
  );
}
